import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import UnfoldMoreIcon from '@assets/buttons-icons/expand-up-down.svg?react';
import palette from '@lib/core/src/theme/palette';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import { Box, Button, Popover, styled } from '@mui/material';
import dayjs from 'dayjs';

const DateRangeFilterButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isFilterActive'
})<{ isFilterActive: boolean }>(({ isFilterActive }) => ({
  borderRadius: '88px',
  padding: '5px 8px',
  border: `1px solid ${palette.grey[1200]}`,
  boxShadow: 'none',
  backgroundColor: isFilterActive ? palette.grey[500] : 'transparent',
  color: isFilterActive ? palette.white : palette.black,
  fontSize: '12px',
  marginRight: '8px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: isFilterActive ? palette.grey[500] : 'transparent',
    boxShadow: 'none'
  },
  '&.Mui-disabled': {
    backgroundColor: 'transparent'
  }
}));

type DateRangeFilterProps = {
  handleFilterClick?: () => void;
  onDateChange: (
    date: [Date | null, Date | null],
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void;
  dateRange: { startDate?: number; endDate?: number };
};

export const DateRangeFilter = ({
  handleFilterClick,
  onDateChange,
  dateRange
}: DateRangeFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [maxDateValue, setMaxDateValue] = useState<Date>(new Date());
  const onFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    handleFilterClick?.();
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    const today = new Date();
    if (!dateRange?.startDate) {
      setMaxDateValue(today);
      return;
    }
    const startDate = new Date(dateRange.startDate);
    const oneMonthAfterStart = dayjs(startDate).add(1, 'month').toDate();
    if (oneMonthAfterStart > today) {
      setMaxDateValue(today);
    } else {
      setMaxDateValue(oneMonthAfterStart);
    }
  }, [dateRange]);

  return (
    <>
      <DateRangeFilterButton
        variant="contained"
        isFilterActive={!!dateRange}
        onClick={onFilterClick}
      >
        <DateRangeOutlinedIcon fontSize="small" sx={{ marginRight: '5px' }} /> Date
        {dateRange
          ? `: ${dayjs(dateRange?.startDate).format('MMM DD,YYYY')} - ${dateRange?.endDate ? dayjs(dateRange?.endDate).format('MMM DD,YYYY') : ''}`
          : ': All'}
        <UnfoldMoreIcon style={{ marginLeft: '5px' }} fontSize="small" />
      </DateRangeFilterButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              ' .react-datepicker__month-container': {
                float: 'unset'
              }
            }
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box>
          <DatePicker
            selected={dateRange.startDate ? new Date(dateRange?.startDate) : new Date()}
            startDate={dateRange.startDate ? new Date(dateRange?.startDate) : new Date()}
            endDate={dateRange.endDate ? new Date(dateRange.endDate) : null}
            onChange={onDateChange}
            maxDate={maxDateValue}
            showDisabledMonthNavigation
            selectsRange
            inline
          />
        </Box>
      </Popover>
    </>
  );
};
